// Profile → notification settings (2026-10-18). Same server-side source of truth as onboarding Step 9.
// Shows the PREFERENCES (per user, server) and THIS DEVICE (OS permission + registration) separately, and never
// implies notifications will reach this device unless permission is granted AND the device is registered AND the
// preference is on. A toggle shows its new state only after the server stored it.
import React, { useCallback, useEffect, useState } from 'react';
import {
  devicePermissionState, fetchNotificationPrefs, saveNotificationPrefs, willReachThisDevice,
} from '../utils/notificationPrefs';
import { enablePushOnThisDevice, getCurrentPushToken } from '../firebase';

const PERMISSION_TEXT = {
  granted: 'מאושרות',
  denied: 'חסומות',
  default: 'עדיין לא אושרו',
  unsupported: 'לא נתמכות בדפדפן הזה',
  ios_needs_install: 'זמינות רק מהאפליקציה המותקנת במסך הבית',
};

const DENIED_HELP = 'ההתראות חסומות במכשיר הזה, ולכן לא יגיעו אליו. כדי לאפשר: בסמסונג/Android — הגדרות ← אפליקציות ← ' +
  'RitmoIQ (או Chrome) ← התראות ← מותר. באייפון — הגדרות ← התראות ← RitmoIQ. אחר כך חזור לכאן.';
const IOS_HELP = 'באייפון התראות עובדות רק מהאפליקציה המותקנת: בספארי שתף ← "הוסף למסך הבית", פתח משם וחזור לכאן.';

// Profile V2 (2026-10-19): one row per preference (>= 56 px), title + one-line description + a switch. The
// control is still the same checkbox (role=switch) — only its look changed.
const SWITCH = 'appearance-none shrink-0 relative w-11 h-[26px] rounded-full cursor-pointer transition-colors ' +
  'bg-bar checked:bg-accent disabled:opacity-50 disabled:cursor-not-allowed ' +
  "before:content-[''] before:absolute before:top-[3px] before:right-[3px] before:w-5 before:h-5 before:rounded-full " +
  'before:bg-text before:transition-transform checked:before:-translate-x-[18px] ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';

function Toggle({ label, hint, checked, disabled, onChange, testId, badge }) {
  return (
    <div className="flex items-center justify-between gap-3 min-h-[56px] py-2.5 border-t border-divider">
      <div className="flex-1 min-w-0 text-right">
        <div className="text-text text-[15px] font-semibold">
          {label}
          {badge && <span className="mr-2 text-[11px] px-1.5 py-0.5 rounded-badge align-middle" data-testid={`${testId}-badge`}
                          style={{ border: '1px solid var(--g-line)', color: 'var(--g-sub)' }}>{badge}</span>}
        </div>
        {hint && <div className="text-text-muted text-[12px] mt-0.5 leading-snug">{hint}</div>}
      </div>
      <input type="checkbox" role="switch" aria-label={label} aria-checked={!!checked} data-testid={testId}
             className={SWITCH} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}

export default function NotificationSettings({
  userId, apiBase,
  fetchPrefs = fetchNotificationPrefs, savePrefs = saveNotificationPrefs, enableDevice = enablePushOnThisDevice,
  permissionState = devicePermissionState, currentToken = getCurrentPushToken,
}) {
  const [view, setView] = useState(null);           // server view: preferences, devices, this_device_registered…
  const [permission, setPermission] = useState(() => permissionState());
  const [token, setToken] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(null);       // key being saved
  const [saveError, setSaveError] = useState('');
  const [deviceMsg, setDeviceMsg] = useState('');

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const t = await currentToken();
      setToken(t);
      setPermission(permissionState());
      setView(await fetchPrefs(apiBase, userId, t));
    } catch {
      setLoadError(true);
    }
  }, [apiBase, userId, fetchPrefs, currentToken, permissionState]);

  useEffect(() => { if (userId) load(); }, [userId, load]);

  const change = async (key, value) => {
    setSaving(key);
    setSaveError('');
    try {
      setView(await savePrefs(apiBase, userId, { [key]: value }, token));   // shown only once stored
    } catch {
      setSaveError('השינוי לא נשמר. נסה שוב.');
    } finally {
      setSaving(null);
    }
  };

  const enableHere = async () => {
    setDeviceMsg('');
    let r = { status: 'register_failed' };
    try { r = await enableDevice(userId); } catch { /* keep */ }
    if (r.status === 'denied') setDeviceMsg(DENIED_HELP);
    else if (r.status === 'dismissed') setDeviceMsg('לא אושרו התראות במכשיר.');
    else if (r.status !== 'registered') setDeviceMsg('רישום המכשיר נכשל. נסה שוב.');
    await load();
  };

  if (loadError) {
    return (
      <div className="text-sm text-right" data-testid="notif-settings-error">
        <span className="text-text-body">לא הצלחנו לטעון את הגדרות ההתראות.</span>{' '}
        <button type="button" className="underline text-text-body" onClick={load}>נסה שוב</button>
      </div>
    );
  }
  if (!view) return <div className="text-text-muted text-sm" data-testid="notif-settings-loading">טוען הגדרות התראות…</div>;

  const prefs = view.preferences;
  const registered = Boolean(view.this_device_registered);
  const reaches = willReachThisDevice({ permission, thisDeviceRegistered: registered, prefs });
  let deviceLine;
  if (!prefs.enabled) deviceLine = 'ההתראות כבויות בהגדרות שלך — לא יישלחו לאף מכשיר.';
  else if (permission === 'denied') deviceLine = DENIED_HELP;
  else if (permission === 'ios_needs_install') deviceLine = IOS_HELP;
  else if (permission === 'unsupported') deviceLine = 'הדפדפן הזה לא תומך בהתראות — הן לא יגיעו למכשיר הזה.';
  else if (!reaches) deviceLine = 'המכשיר הזה עדיין לא רשום להתראות, ולכן הן לא יגיעו אליו.';
  else deviceLine = 'התראות שהפעלת יגיעו למכשיר הזה.';

  const canEnableHere = prefs.enabled && (permission === 'default' || (permission === 'granted' && !registered));

  return (
    <div dir="rtl" data-testid="notif-settings">
      {/* This device: permission + registration facts and the only "register this device" control (compact). */}
      <div className="pb-3 text-[13px] leading-relaxed" data-testid="notif-device-status">
        <div className="flex items-start gap-2">
          <span aria-hidden="true" className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${reaches ? 'bg-positive' : 'bg-text-muted'}`} />
          <div className="min-w-0 text-text-body">
            <div>התראות במכשיר הזה: <b data-testid="notif-permission">{PERMISSION_TEXT[permission]}</b>
              {' · '}רישום המכשיר: <b data-testid="notif-registered">{registered ? 'רשום' : 'לא רשום'}</b>
              {' · '}מכשירים רשומים בחשבון: {view.devices_registered}</div>
            <div className="mt-0.5 text-text-muted text-[12px]" data-testid="notif-device-line">{deviceLine}</div>
          </div>
        </div>
        {canEnableHere && (
          <button type="button" onClick={enableHere} data-testid="notif-enable-here"
                  className="mt-2.5 w-full h-11 rounded-control btn-primary text-[15px]">
            {permission === 'default' ? 'אפשר התראות במכשיר הזה' : 'רשום את המכשיר הזה'}
          </button>
        )}
        {deviceMsg && <div className="mt-1.5 text-[12px]" style={{ color: 'var(--g-sub)' }} data-testid="notif-device-msg">{deviceMsg}</div>}
      </div>

      <Toggle label="קבלת התראות" testId="pref-enabled" checked={prefs.enabled} disabled={saving !== null}
              onChange={(v) => change('enabled', v)} hint="מתג ראשי לכל ההתראות, בכל המכשירים." />
      <Toggle label="התראות אימון" testId="pref-training" checked={prefs.training}
              disabled={saving !== null || !prefs.enabled} onChange={(v) => change('training', v)}
              hint="כרגע: התראת בוקר ביום מרוץ. תזכורות לאימונים מתוכננים ועדכונים על שינוי באימון עדיין לא זמינים." />
      <Toggle label="ניתוח המאמן" testId="pref-coach" checked={prefs.coach_analysis}
              disabled={saving !== null || !prefs.enabled} onChange={(v) => change('coach_analysis', v)}
              badge={view.garmin_push_available ? null : 'עדיין לא זמין ל-Garmin'}
              hint={view.garmin_push_available
                ? 'התראה כשהניתוח של אימון שהסתיים מוכן.'
                : 'התראה כשהניתוח של אימון שהסתיים מוכן. עבור Garmin ההתראות עדיין לא מופעלות במערכת — הבחירה נשמרת ותחול כשיופעלו.'} />
      {saveError && <div className="text-quality text-[12px] mt-1" role="alert" data-testid="notif-save-error">{saveError}</div>}
    </div>
  );
}
